<template>
  <div class="thread">
    <div class="thread-card m-2">
      <div class="card-body" v-if="form">
        <form @submit.prevent="onSubmit">
          <div class="form-group">
            <!-- Title -->
            <div class="row p-1">
              <div class="col-sm-8">
                <input
                  type="text"
                  v-model="form.name"
                  name="name"
                  class="form-control"
                  id="name"
                  placeholder="Title"
                />
              </div>
            </div>

            <!-- Stock & Target -->
            <div class="row p-1">
              <div class="col-sm-4">
                <MultiSelectFilter
                  v-model="form.stock"
                  label="Select stock"
                  :options="stockOption"
                  name="stock"
                />
              </div>

              <div class="col-sm-4">
                <v-select
                  taggable
                  placeholder="Select Target"
                  class="custom-select mr-sm-2"
                  id="select-target"
                  name="target"
                  v-model="form.target"
                  item-title="label"
                  item-value="value"
                  :items="target"
                  density="compact"
                />
              </div>
            </div>

            <!-- Rule & Values -->
            <div class="row p-1">
              <div class="col-sm-4">
                <v-select
                  taggable
                  placeholder="Select Rule"
                  class="custom-select mr-sm-2"
                  id="select-rule"
                  name="rule"
                  v-model="form.rule"
                  item-title="label"
                  item-value="value"
                  :items="filter_rule"
                  density="compact"
                />
              </div>

              <div v-if="form.rule" class="col-sm-4">
                <div v-if="day" class="day mb-2">
                  <input
                    type="number"
                    v-model="form.value.day"
                    class="form-control"
                    placeholder="Day"
                  />
                </div>

                <div v-if="!between" class="value mb-2">
                  <input
                    type="number"
                    v-model="form.value.value"
                    class="form-control"
                    placeholder="Value"
                  />
                </div>

                <div v-if="between" class="between mb-2">
                  <input
                    type="number"
                    v-model="form.value.from"
                    class="form-control mb-1"
                    placeholder="From"
                  />
                  <input
                    type="number"
                    v-model="form.value.to"
                    class="form-control"
                    placeholder="To"
                  />
                </div>
              </div>
            </div>

            <!-- Buttons -->
            <div class="row p-1">
              <div class="col-sm-1">
                <button :disabled="v$.form.$invalid" type="submit" class="btn btn-primary">
                  Submit
                </button>
              </div>
              <div class="col-sm-1">
                <router-link to="/tracker/list">
                  <button type="button" class="btn btn-outline-link">Back</button>
                </router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useAlertStore } from "@/stores";
import gql from "graphql-tag";
import { MultiSelectFilter } from "@/components/Report/filter/index";

const ADD_THREAD_QUERY = gql`
  mutation addThread(
    $threadId: ID
    $name: String
    $stock: [String]
    $target: String
    $rule: String
    $value: ValueOption
  ) {
    addThread(
      thread_id: $threadId
      name: $name
      stock: $stock
      target: $target
      rule: $rule
      value: $value
    ) {
      thread_id
      name
      type_id
      rule
      status
    }
  }
`;

const THREAD_ROW_QUERY = gql`
  query Thread($threadId: ID!) {
    thread(thread_id: $threadId) {
      thread_id
      name
      type_id
      rule
      status
    }
  }
`;

const STOCK_OPTION = gql`
  query StockOption {
    stocks {
      stock_id
      name
      platform_id
    }
  }
`;

export default {
  name: "ThreadView",
  components: { MultiSelectFilter },
  setup() {
    return {
      v$: useVuelidate(),
      alertStore$: useAlertStore(),
    };
  },
  data() {
    return {
      stocks: [],
      stockOption: [],
      thread: [],
      between: false,
      day: false,
      form: {
        threadId: 0,
        name: "",
        stock: [],
        target: null,
        rule: null,
        status: 1,
        type_id: 1,
        value: {
          value: null,
          from: null,
          to: null,
          day: null,
        },
      },
      target: [
        { label: "Price", value: "market_price" },
        { label: "52 Week Low", value: "fifty_two_week_low" },
        { label: "52 Week High", value: "fifty_two_week_high" },
      ],
      rule: [
        { label: "=", value: "=" },
        { label: ">", value: ">" },
        { label: "<", value: "<" },
        { label: "Between", value: "between" },
      ],
      filter_rule: [],
    };
  },
  validations() {
    return {
      form: {
        stock: { required },
        target: { required },
        rule: { required },
      },
    };
  },
  methods: {
    getStockOptionList() {
      return this.stocks.map((stock) => ({
        label: `${stock.name} - ${stock.platform_id}`,
        value: String(stock.stock_id),
      }));
    },
    onSubmit() {
      this.v$.$validate().then((valid) => {
        if (!valid) return;

        this.$apollo
          .mutate({
            mutation: ADD_THREAD_QUERY,
            variables: {
              threadId: this.form.threadId,
              name: this.form.name,
              stock: this.form.stock,
              target: this.form.target,
              rule: this.form.rule,
              value: this.form.value,
            },
          })
          .then(() => {
            const message = this.form.threadId
              ? "Thread updated successfully."
              : "New thread added successfully.";
            this.alertStore$.success(message);
            this.$router.push("/tracker/list");
          })
          .catch((error) => {
            this.alertStore$.error(error.message);
          });
      });
    },
  },
  watch: {
    thread(newThread) {
      const parsed = newThread.rule ? JSON.parse(newThread.rule) : {};
      const ruleCode = parsed.rule || "";
      const targetCode = parsed.target || "";

      this.between = ruleCode === "between";
      this.form = {
        threadId: newThread.thread_id || 0,
        name: newThread.name || "",
        stock: parsed.stock || [],
        rule: parsed.rule || null,
        target: parsed.target || null,
        status: newThread.status || 1,
        type_id: newThread.type_id || 1,
        value: {
          value: parsed.value?.value || null,
          from: parsed.value?.from || null,
          to: parsed.value?.to || null,
          day: parsed.value?.day || null,
        },
      };

      this.filter_rule = ["fifty_two_week_low", "fifty_two_week_high"].includes(targetCode)
        ? [{ value: "between", label: "Between" }]
        : this.rule;
    },
    "form.rule"(newVal) {
      this.between = newVal === "between";
    },
    "form.target"(newVal) {
      if (["fifty_two_week_low", "fifty_two_week_high"].includes(newVal)) {
        this.filter_rule = [{ value: "between", label: "Between" }];
      } else {
        this.filter_rule = this.rule;
      }
      //this.form.rule = null;
    },
    stocks() {
      this.stockOption = this.getStockOptionList();
    },
  },
  apollo: {
    stocks: {
      query: STOCK_OPTION,
      error(error) {
        this.alertStore$.error(error.message);
      },
    },
    thread: {
      query: THREAD_ROW_QUERY,
      variables() {
        return {
          threadId: this.$route.query.id,
        };
      },
      skip() {
        return !this.$route.query.id;
      },
      error(error) {
        this.alertStore$.error(error.message);
      },
    },
  },
};
</script>

<style scoped>
.thread-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px;
}
</style>
